import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Book, TitlePageSettings } from '../types';
import { getGraphQLClient, GET_BOOK, UPDATE_BOOK } from '../lib/graphql';
import { captureException } from '../lib/sentry';
import { FrontMatterEditorLayout } from '../components/front-matter/FrontMatterEditorLayout';

export default function TitlePageEditorPage() {
  const { t } = useTranslation();
  const { bookId } = useParams<{ bookId: string }>();
  const [book, setBook] = useState<Book | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [isVisible, setIsVisible] = useState(true);
  const [titleStyle, setTitleStyle] = useState<'centered' | 'left' | 'right'>('centered');
  const [showSubtitle, setShowSubtitle] = useState(true);
  const [showAuthor, setShowAuthor] = useState(true);
  const [customAuthorName, setCustomAuthorName] = useState('');

  useEffect(() => {
    const loadBook = async () => {
      if (!bookId) return;

      try {
        const client = getGraphQLClient();
        const data = await client.request<{ book: Book }>(GET_BOOK, { id: bookId });
        setBook(data.book);

        // Initialize form state from book settings
        const settings = data.book.frontMatterSettings?.titlePage;
        setIsVisible(settings?.visible ?? true);
        setTitleStyle(settings?.titleStyle ?? 'centered');
        setShowSubtitle(settings?.showSubtitle ?? true);
        setShowAuthor(settings?.showAuthor ?? true);
        setCustomAuthorName(settings?.customAuthorName ?? '');
      } catch (err) {
        captureException(err);
        setError(t('common.error'));
      } finally {
        setIsLoading(false);
      }
    };

    loadBook();
  }, [bookId, t]);

  const handleSave = async () => {
    if (!bookId) return;

    setIsSaving(true);
    setError(null);

    try {
      const client = getGraphQLClient();
      const titlePageSettings: TitlePageSettings = {
        visible: isVisible,
        titleStyle,
        showSubtitle,
        showAuthor,
        customAuthorName: customAuthorName || undefined,
      };

      await client.request(UPDATE_BOOK, {
        id: bookId,
        frontMatterSettings: {
          titlePage: titlePageSettings,
        },
      });
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const authorName = customAuthorName || book?.user?.name || 'Author';
  const alignmentClass = {
    centered: 'text-center items-center',
    left: 'text-left items-start',
    right: 'text-right items-end',
  }[titleStyle];

  return (
    <FrontMatterEditorLayout
      title={t('frontMatter.titlePage')}
      bookId={bookId!}
      isLoading={isLoading}
      isSaving={isSaving}
      isVisible={isVisible}
      onVisibilityChange={setIsVisible}
      onSave={handleSave}
      error={error}
      preview={
        <div className={`flex flex-col ${alignmentClass} px-8 w-full`}>
          <div className="w-16 h-0.5 bg-gray-300 dark:bg-gray-600 mb-8" />
          <h1 className="font-serif text-3xl md:text-4xl font-bold leading-tight mb-4 text-gray-900 dark:text-gray-100">
            {book?.title || 'Book Title'}
          </h1>
          {showSubtitle && book?.subtitle && (
            <h2 className="font-serif text-lg text-gray-600 dark:text-gray-400 italic mb-6">
              {book.subtitle}
            </h2>
          )}
          {showAuthor && (
            <>
              <div className="w-8 h-0.5 bg-gray-300 dark:bg-gray-600 my-6" />
              <p className="font-serif text-lg text-gray-700 dark:text-gray-300">{authorName}</p>
            </>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        {/* Alignment */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('frontMatter.alignment')}
          </label>
          <div className="flex gap-2">
            {(['left', 'centered', 'right'] as const).map((style) => (
              <button
                key={style}
                onClick={() => setTitleStyle(style)}
                className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                  titleStyle === style
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {t(`frontMatter.alignment.${style}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Show subtitle toggle */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {t('frontMatter.showSubtitle')}
          </span>
          <button
            type="button"
            onClick={() => setShowSubtitle(!showSubtitle)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
              showSubtitle ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                showSubtitle ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Show author toggle */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {t('frontMatter.showAuthor')}
          </span>
          <button
            type="button"
            onClick={() => setShowAuthor(!showAuthor)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
              showAuthor ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                showAuthor ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Custom author name */}
        {showAuthor && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('frontMatter.customAuthorName')}
            </label>
            <input
              type="text"
              value={customAuthorName}
              onChange={(e) => setCustomAuthorName(e.target.value)}
              placeholder={book?.user?.name || t('frontMatter.authorName')}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
              {t('frontMatter.customAuthorNameHint')}
            </p>
          </div>
        )}
      </div>
    </FrontMatterEditorLayout>
  );
}
